import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS, CAMPAIGN_REQUEST_KEYS} from "@/api/campaignApplicationApi";
import {useUpdateCreditsCache} from "@/api/companyApi";
import InfoIcon from "@/assets/colorable/info.svg?react";
import RefluencedCreditIcon from "@/assets/colorable/refluenced_credit.svg?react";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import MarkdownEditor from "@/components/Common/MarkdownEditor";
import Modal from "@/components/Common/Modal";
import usePackageLimits from "@/hooks/usePackageLimits";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import {useUserAvatarPlaceholder} from "@/hooks/useUserAvatarPlaceholder";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import {cancelApplicationModalDataAtom} from "@/store/jotaiAtoms";
import {useMutation} from "@tanstack/react-query";
import {useAtom} from "jotai";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const CancelApplicationModal: FC = () => {
  const UserAvatarPlaceholder = useUserAvatarPlaceholder();
  const {t} = useTranslation(["campaign_preparation", "common"]);
  const {credits} = usePackageLimits();
  const formatPrice = usePriceFormat();
  const searchParam = useCampaignInfluencerFilterStore(state => state.filters.search);
  const [cancelApplicationModalData, setCancelApplicationModalData] = useAtom(cancelApplicationModalDataAtom);
  const application = cancelApplicationModalData?.application!;
  const campaign = cancelApplicationModalData?.campaign!;
  const influencer = cancelApplicationModalData?.influencer!;
  const canCancel = application.can_be_cancelled_directly;
  const showInvalidateVoucherWarning = campaign.service_is_voucher && !!application.voucher;
  const isCreditRefund = application.payment_source === "invoice";
  const [reason, setReason] = useState("");
  const updateCreditsCache = useUpdateCreditsCache();
  const {mutate: cancelApplication, isPending: isCancelingApplication} = useMutation({
    meta: {successMessage: t("application_removal_success")},
    mutationKey: CAMPAIGN_APPLICATION_KEYS.mApplicationCancel(application.id),
    mutationFn: () => client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationCancel(application.id)[0], {reason}),
    onSuccess: () => {
      capturePostHogEvent(PostHogEvents.ApplicationCancelled, {
        applicationId: application.id,
        campaignId: campaign.id,
        isCreditRefund,
        isUgc: !!campaign.ugc,
        refundAmount: application.refund_amount ?? 0,
      });

      if (isCreditRefund && application.refund_amount) {
        updateCreditsCache(application.refund_amount);
      }

      [
        CAMPAIGN_KEYS.qCampaignManagement(campaign.id),
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, searchParam),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, true, searchParam),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, false, searchParam),
        CAMPAIGN_KEYS.qCampaignPriceSummary(campaign.id),
        CAMPAIGN_KEYS.qCampaignCancelledApplications(campaign.id),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
    },
  });
  const {mutate: requestCancellation, isPending: isRequestingCancellation} = useMutation({
    meta: {successMessage: t("cancellation_request_sent")},
    mutationKey: CAMPAIGN_REQUEST_KEYS.mBrandCancellationRequest(application.id),
    mutationFn: () =>
      client.post(CAMPAIGN_REQUEST_KEYS.mBrandCancellationRequest(application.id)[0], {
        brand_request_reason: reason,
        campaign_application: application.id,
        request_type: "BRAND_CANCELLATION",
      }),
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, searchParam),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, true, searchParam),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, false, searchParam),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
      setCancelApplicationModalData(null);
    },
  });

  const renderRefundPanel = (isContingent: boolean) =>
    !!application.refund_amount && (
      <div className="bg-primary-light-solid text-neutral-black flex w-full flex-col items-center gap-2.5 rounded-sm p-3">
        {isCreditRefund ? (
          <>
            <span className="text-xs">{t(isContingent ? "credits_if_approved" : "you_will_receive")}</span>
            <div className="flex items-center gap-1">
              <RefluencedCreditIcon className="size-4 shrink-0" />
              <span className="text-2.5xl leading-7 font-semibold">
                {t("common:x_credits", {count: application.refund_amount})}
              </span>
            </div>
            <span className="text-xs">{`${t("common:new_balance")} ${Math.round((application.refund_amount + credits) * 100) / 100}`}</span>
            <div className="bg-neutral-grey-lightest flex w-full items-center gap-2 rounded-md p-3">
              <InfoIcon className="text-primary-sky size-3.5 shrink-0" />
              <span className="text-xs">{t("remove_influencer_refund_info_credit")}</span>
            </div>
          </>
        ) : (
          <>
            <span className="text-xs">{t(isContingent ? "refund_if_approved" : "you_will_be_refunded")}</span>
            <span className="text-2.5xl leading-7 font-semibold">{formatPrice(application.refund_amount)}</span>
            <div className="bg-neutral-grey-lightest flex w-full items-center gap-2 rounded-md p-3">
              <InfoIcon className="text-primary-sky size-3.5 shrink-0" />
              <span className="text-xs">{t("remove_influencer_refund_info")}</span>
            </div>
          </>
        )}
      </div>
    );

  if (canCancel) {
    return (
      <Modal
        onClose={() => setCancelApplicationModalData(null)}
        onConfirm={() => cancelApplication()}
        modalTitle={t(campaign.ugc ? "remove_content_creator" : "remove_influencer")}
        confirmButtonText={t(campaign.ugc ? "remove_content_creator" : "remove_influencer")}
        isConfirmDisabled={!reason || isCancelingApplication}
        isLoading={isCancelingApplication}
        backgroundClassName="max-w-175"
        shouldLockBodyScroll={false}>
        <div className="flex flex-col items-center gap-5">
          <div className="flex flex-col items-center gap-2.5">
            <div className="flex items-center gap-1.5">
              <img
                src={influencer.picture320 ?? influencer.picture ?? UserAvatarPlaceholder}
                className="size-7 shrink-0 rounded-full"
              />
              <span className="text-xl font-semibold">{influencer.first_name}</span>
            </div>
            <span className="text-neutral-black text-center text-sm font-semibold">
              {t("remove_from_campaign", {
                campaign_name: campaign.title,
              })}
            </span>
          </div>
          {renderRefundPanel(false)}
          <div className="bg-warning-light-yellow flex items-center gap-2 self-stretch rounded-md p-4">
            <WarningIcon className="text-warning-dark-yellow size-3.5 shrink-0" />
            <div className="flex flex-col gap-1">
              <span className="text-netural-black text-sm font-semibold">
                {t("influencer_removal_modal_warning_title")}
              </span>
              <span className="text-neutral-black text-xs">
                {t(
                  campaign.ugc
                    ? "influencer_removal_modal_warning_description_ugc"
                    : "influencer_removal_modal_warning_description",
                )}
              </span>
            </div>
          </div>
          {showInvalidateVoucherWarning && (
            <div className="bg-warning-light-yellow flex items-center gap-2 self-stretch rounded-md p-4">
              <WarningIcon className="text-warning-dark-yellow size-3.5 shrink-0" />
              <div className="flex flex-col gap-1">
                <span className="text-netural-black text-sm font-semibold">
                  {t(
                    campaign.ugc
                      ? "influencer_removal_modal_voucher_warning_title_ugc"
                      : "influencer_removal_modal_voucher_warning_title",
                  )}
                </span>
                <span className="text-neutral-black text-xs">
                  {t(
                    campaign.ugc
                      ? "influencer_removal_modal_voucher_warning_description_ugc"
                      : "influencer_removal_modal_voucher_warning_description",
                  )}
                </span>
              </div>
            </div>
          )}
          <div className="flex w-full flex-col gap-2 self-start">
            <div className="flex flex-col gap-1">
              <label className="text-neutral-black text-xs">
                {t(campaign.ugc ? "influencer_removal_reason_label_ugc" : "influencer_removal_reason_label")}
              </label>
              <MarkdownEditor
                className="md-editor-text-xs border-neutral-grey-lightest divide-neutral-grey-lightest h-37.5 max-h-100 border shadow-none"
                placeholder={t("influencer_removal_reason_placeholder")}
                onChange={value => setReason(value ?? "")}
                value={reason}
                visibleDragbar={false}
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      onClose={() => setCancelApplicationModalData(null)}
      onConfirm={() => requestCancellation()}
      modalTitle={t("request_cancellation")}
      confirmButtonText={t("request_cancellation")}
      isConfirmDisabled={!reason || isRequestingCancellation}
      isLoading={isRequestingCancellation}
      backgroundClassName="max-w-175"
      shouldLockBodyScroll={false}>
      <div className="flex flex-col items-center gap-5">
        <div className="flex flex-col items-center gap-2">
          <span className="text-neutral-black text-center text-sm font-semibold">
            {t("request_cancellation_description")}
          </span>
          <div className="bg-warning-light-yellow flex flex-col gap-1 self-stretch rounded-md p-3">
            <div className="text-warning-dark-yellow flex items-center gap-2 text-sm font-semibold">
              <WarningIcon className="size-3.5 shrink-0" />
              {t("request_cancellation")}
            </div>
            <span className="text-neutral-black text-xs">{t("cancellation_request_pending_description")}</span>
          </div>
        </div>
        {renderRefundPanel(true)}
        <div className="flex w-full flex-col gap-2 self-start">
          <div className="flex flex-col gap-1">
            <label className="text-neutral-black text-xs">{t("request_cancellation_reason_label")}</label>
            <MarkdownEditor
              className="md-editor-text-xs border-neutral-grey-lightest divide-neutral-grey-lightest h-37.5 max-h-100 border shadow-none"
              placeholder={t("influencer_removal_reason_placeholder")}
              onChange={value => setReason(value ?? "")}
              value={reason}
              visibleDragbar={false}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CancelApplicationModal;
